import { emptyEntry, newCrew, type CrewEntry, type LogEntry } from "./boardbook-model";
import { getSunrise, getSunset } from "sunrise-sunset-js";
import { addOneDay, crossesMidnight, toIsoDateTime } from "./boardbook-mapping";
import { isValidTimerPosition } from "../flight-timer/timer-position";

/** Flight-timer facts needed to build a board-book entry (epoch ms, as `FlightTimerInterface` holds them). */
export interface TimerFacts {
  takeoffTime: number;
  landingTime: number;
  startPlace: string;
  landingPlace: string;
  landingFuelOnBoard: string;
  remarks: string;
  takeoffFuelOnBoard?: number;
  fuelAdded?: number;
  afterLandingRefuel?: string;
  startIcao?: string;
  landingIcao?: string;
  startPoint?: number[];
  landingPoint?: number[];
}

/** Subset of `flightlistStruct` used to enrich a timer-derived entry. */
export interface FlightFacts {
  flightNumber?: string;
  icaoOrigin?: string;
  icaoDest?: string;
  pilotInCommand?: string;
  flightrules?: string;
  crew?: CrewEntry[];
  passengers?: number | null;
}

/** Same 30-minute twilight margin used by the flight planning night check. */
function nightAt(time: number, position: number[] | undefined): boolean | null {
  if (!isValidTimerPosition(position)) return null;
  const date = new Date(time);
  const dawn = getSunrise(position[1], position[0], date).getTime() - 30 * 60000;
  const dusk = getSunset(position[1], position[0], date).getTime() + 30 * 60000;
  if (!Number.isFinite(dawn) || !Number.isFinite(dusk)) return null;
  return time < dawn || time > dusk;
}

function utcDatePart(epochMs: number): string {
  const d = new Date(epochMs);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function utcTimePart(epochMs: number): string {
  const d = new Date(epochMs);
  const h = String(d.getUTCHours()).padStart(2, "0");
  const min = String(d.getUTCMinutes()).padStart(2, "0");
  return `${h}:${min}`;
}

function resolvedPlace(fromFlight: string | undefined, fromTimer: string, recordedIcao?: string): string {
  if (fromTimer === "mid-air") return "AIR";
  const code = (recordedIcao ?? (fromFlight?.trim() || fromTimer?.split(",")[0]?.trim() || "")).toUpperCase();
  return /^[A-Z]{4}$/.test(code) ? code : "ZZZZ";
}

/**
 * The entry id of a timer leg. Every device of the aircraft has the same takeoff
 * time for a leg, so two devices that stop the same leg write one entry.
 * Returns "" when the registration or the takeoff time is missing.
 */
export function timerEntryId(registration: string | undefined, takeoffTime: number): string {
  const reg = (registration ?? "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  return reg && takeoffTime > 0 ? `timer-${reg}-${Math.round(takeoffTime)}` : "";
}

/**
 * Builds a board-book `LogEntry` from a stopped flight timer, folding in whatever
 * flight-list facts are on hand. Returns `null` when the timer never recorded a
 * completed flight (no takeoff, or landing not after takeoff).
 */
export function entryFromTimer(timer: TimerFacts, flight?: FlightFacts, blockOffsetMinutes = 2): LogEntry | null {
  if (timer.takeoffTime <= 0 || timer.landingTime <= timer.takeoffTime) {
    return null;
  }

  const entry = emptyEntry();
  const pic = flight?.pilotInCommand?.trim();
  const rule = flight?.flightrules?.trim().toUpperCase();

  const offset = Number.isFinite(blockOffsetMinutes) ? Math.max(0, blockOffsetMinutes) * 60000 : 0;
  const departureOffset = timer.startPlace === "mid-air" ? 0 : offset;
  const arrivalOffset = timer.landingPlace === "mid-air" ? 0 : offset;
  entry.date = utcDatePart(timer.takeoffTime - departureOffset);
  entry.blockOff = utcTimePart(timer.takeoffTime - departureOffset);
  entry.takeoff = utcTimePart(timer.takeoffTime);
  entry.blockOn = utcTimePart(timer.landingTime + arrivalOffset);
  entry.landing = utcTimePart(timer.landingTime);

  entry.departure = resolvedPlace(flight?.icaoOrigin, timer.startPlace, timer.startIcao);
  entry.destination = resolvedPlace(flight?.icaoDest, timer.landingPlace, timer.landingIcao);

  entry.departureName = entry.departure === "ZZZZ" ? (timer.startPlace ?? "").trim() : "";
  entry.destinationName = entry.destination === "ZZZZ" ? (timer.landingPlace ?? "").trim() : "";

  entry.flightNumber = flight?.flightNumber ?? "";
  entry.flightRule = rule?.startsWith("I") || rule?.startsWith("Y") ? "IFR" : "VFR";
  entry.landings = timer.landingPlace === "mid-air" ? 0 : 1;

  // Timer/live quantities are kilograms. Uplift is additional fuel, not total FOB.
  const fuel = parseFloat(timer.landingFuelOnBoard);
  entry.fuelAtLanding = Number.isFinite(fuel) && fuel >= 0 ? fuel : null;
  entry.fuelAtTakeoff =
    Number.isFinite(timer.takeoffFuelOnBoard) && timer.takeoffFuelOnBoard >= 0 ? timer.takeoffFuelOnBoard : null;
  entry.fuelUplBefore = 0;
  const afterRefuel = Number(timer.afterLandingRefuel);
  entry.fuelUplAfter =
    Number.isFinite(timer.fuelAdded) && timer.fuelAdded >= 0
      ? timer.fuelAdded
      : timer.afterLandingRefuel?.trim() &&
          Number.isFinite(afterRefuel) &&
          entry.fuelAtLanding != null &&
          afterRefuel >= entry.fuelAtLanding
        ? afterRefuel - entry.fuelAtLanding
        : 0;

  entry.remarks = timer.remarks ?? "";
  entry.crew = flight?.crew?.length
    ? structuredClone(flight.crew)
    : pic
      ? [{ ...newCrew("PIC"), familyName: pic }]
      : [];

  entry.passengers = Number.isInteger(flight?.passengers) && flight.passengers >= 0 ? flight.passengers : null;
  // Leg timestamps are stored at minute precision. Use those same boundaries
  // and retain full precision: rounding 23 minutes to 0.38/0.39 can exceed the cap.
  const minute = (time: number) => Math.floor(time / 60000) * 60000;
  const flightMs = minute(timer.landingTime) - minute(timer.takeoffTime);
  const blockMs = minute(timer.landingTime + arrivalOffset) - minute(timer.takeoffTime - departureOffset);
  const hours = (ms: number) => ms / 3600000;
  const takeoffNight = nightAt(timer.takeoffTime, timer.startPoint);
  const landingNight = nightAt(timer.landingTime, timer.landingPoint);
  // Estimate night duration along the recorded endpoints, with one-minute samples.
  let nightMs = 0;
  let knownNight = isValidTimerPosition(timer.startPoint) && isValidTimerPosition(timer.landingPoint);
  if (knownNight) {
    for (let at = timer.takeoffTime; at < timer.landingTime; at += 60000) {
      const end = Math.min(at + 60000, timer.landingTime);
      const fraction = ((at + end) / 2 - timer.takeoffTime) / (timer.landingTime - timer.takeoffTime);
      const position = timer.startPoint.map((v, i) => v + (timer.landingPoint[i] - v) * fraction);
      const night = nightAt((at + end) / 2, position);
      if (night === null) {
        knownNight = false;
        break;
      }
      if (night) nightMs += end - at;
    }
  }
  for (const member of entry.crew) {
    member.flightTime ??= hours(flightMs);
    member.blockTime ??= hours(blockMs);
    if (timer.startPlace !== "mid-air" && takeoffNight !== null) {
      member.takeoffsDay ??= takeoffNight ? 0 : 1;
      member.takeoffsNight ??= takeoffNight ? 1 : 0;
    }
    if (timer.landingPlace !== "mid-air" && landingNight !== null) {
      member.landingsDay ??= landingNight ? 0 : 1;
      member.landingsNight ??= landingNight ? 1 : 0;
    }
    if (knownNight) member.nightTime ??= hours(Math.min(nightMs, flightMs));
    if (rule === "IFR") member.ifrTime ??= hours(flightMs);
    else if (rule === "VFR") member.ifrTime ??= 0;
  }

  return entry;
}

/** UTC date and instant of a clock time of a leg. A time before block-off is on the next day. */
function legInstant(entry: LogEntry, hhmm: string): { date: string; ms: number } {
  const date = crossesMidnight(entry.blockOff, hhmm) ? addOneDay(entry.date) : entry.date;
  return { date, ms: Date.parse(toIsoDateTime(date, hhmm)) };
}

/**
 * Moves the block-off of a timer leg up to the block-on of the leg before it, when the
 * two block windows overlap only because of the block offsets. A real overlap, where the
 * earlier leg is still on the ground-run at this takeoff, stays unchanged.
 */
export function clampBlockOff(entry: LogEntry, entries: LogEntry[]): LogEntry {
  const off = Date.parse(toIsoDateTime(entry.date, entry.blockOff));
  const takeoff = legInstant(entry, entry.takeoff).ms;
  let best: { date: string; ms: number; hhmm: string } | null = null;
  for (const other of entries) {
    if (other.id === entry.id) continue;
    const on = legInstant(other, other.blockOn);
    if (on.ms > off && on.ms <= takeoff && (!best || on.ms > best.ms)) {
      best = { ...on, hhmm: other.blockOn };
    }
  }
  if (!best) return entry;
  // The crew block time must shrink with the leg, or the crew time cap refuses the leg.
  const shiftHours = (best.ms - off) / 3600000;
  const crew = entry.crew.map((member) =>
    member.blockTime == null ? member : { ...member, blockTime: Math.max(0, member.blockTime - shiftHours) }
  );
  return { ...entry, date: best.date, blockOff: best.hhmm, crew };
}
